import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledActionCard,
  StyledActionWarpper,
  StyledDeleteButton,
  StyledDiv,
  StyledRow,
} from '../styles';

import { ReorderButtons } from 'components/ReorderButtons';
import Select from 'components/Inputs/Select';
import { useErrors } from '../useErrors';
import { SharingSettingsBlock } from './SharingSettingsBlock';
import { NameInput } from './NameInput';
import { DescriptionInput } from './DescriptionInput';

export const CustomActionCard = ({
  id,
  name,
  description,
  action,
  automation,
  sharing_settings,
  actionOptions,
  automationOptions,
  order,
  index,
  isLastIndex,
  isFirstError,
  onReorder,
  onDelete,
  onChange,
  errors,
  setErrors,
}) => {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);

  const { hasError, errorKey, validate, clearError } = useErrors({
    id,
    errors,
    setErrors,
    enabled: isFirstError,
  });

  useEffect(() => {
    if (hasError) {
      wrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [hasError]);

  const onChangeAction = useCallback(
    ({ value }) => {
      onChange(id, 'action', value);
      clearError('action');
    },
    [onChange, id, clearError]
  );

  const onChangeAutomation = useCallback(
    ({ value, label }) => {
      onChange(id, 'automation', { id: value, name: label });
      clearError('automation');
    },
    [onChange, id, clearError]
  );

  const handleDelete = () => {
    onDelete(id);
  };

  const automationValue = useMemo(() => {
    return automation ? { label: automation.name, value: automation.id } : null;
  }, [automation]);

  return (
    <StyledActionWarpper
      ref={wrapperRef}
      hasError={hasError}
      data-ga-action-id={id}
      data-qa-action-index={order}
    >
      <ReorderButtons
        index={index}
        isFirst={index === 0}
        isLast={isLastIndex}
        onReorder={onReorder}
      />
      <StyledActionCard>
        <StyledRow>
          <StyledDiv width={834}>
            <NameInput
              id={id}
              name={name}
              onChange={onChange}
              error={errors?.name}
              validate={errorKey === 'name' ? validate : undefined}
              clearError={clearError}
            />
          </StyledDiv>
          <StyledDeleteButton color="red" variant="text" onClick={handleDelete}>
            {t('Delete Action')}
          </StyledDeleteButton>
        </StyledRow>
        <StyledRow>
          <StyledDiv width={407}>
            <Select
              value={action}
              label={t('Action')}
              options={actionOptions}
              onChange={onChangeAction}
              error={errors?.action}
              validate={errorKey === 'action' ? validate : undefined}
            />
          </StyledDiv>
          <StyledDiv width={407}>
            <Select
              value={automationValue}
              label={t('Automation')}
              placeholder={t('Choose Automation')}
              options={automationOptions}
              onChange={onChangeAutomation}
              error={errors?.automation}
              validate={errorKey === 'automation' ? validate : undefined}
            />
          </StyledDiv>
        </StyledRow>
        <StyledRow>
          <StyledDiv width={834}>
            <DescriptionInput
              id={id}
              description={description}
              onChange={onChange}
              error={errors?.description}
              validate={errorKey === 'description' ? validate : undefined}
              clearError={clearError}
            />
          </StyledDiv>
        </StyledRow>
        <SharingSettingsBlock
          id={id}
          sharingSettings={sharing_settings}
          onChange={onChange}
          clearError={clearError}
        />
      </StyledActionCard>
    </StyledActionWarpper>
  );
};
